'use client';
import React, { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import type { Address } from 'viem';
import { useSignMessage } from 'wagmi';
import { SignatureContext } from '../context/SignatureContext';
import 'react-toastify/dist/ReactToastify.css';

export default function ConfirmWalletButton({ address }: { address: Address }) {
  const { signMessageAsync } = useSignMessage();
  const { setSignature, setAiAddress } = useContext(SignatureContext);
  const [isSigning, setIsSigning] = React.useState<boolean>(false);

  const handleSignMessage = useCallback(async () => {
    setIsSigning(true);
    toast.info('Initiating wallet confirmation...', { autoClose: 3000 });
    try {
      const message = `Please sign this message to verify ownership of wallet: ${address.toLowerCase()}`;
      const signature = await signMessageAsync({ message });

      // Call the API endpoint to set the auth token
      const response = await fetch('/api/auth/set-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ signature, address }),
      });

      if (!response.ok) {
        throw new Error('Failed to set auth token');
      }

      setSignature(signature);
      toast.success('Wallet ownership confirmed!', { autoClose: 3000 });
    } catch (error: any) {
      console.error('Error signing message:', error);
      toast.error('Failed to confirm wallet ownership. Please try again.', {
        autoClose: 5000,
      });
    } finally {
      setIsSigning(false);
    }
  }, [address, signMessageAsync, setSignature]);

  return (
    <button
      onClick={handleSignMessage}
      className={`rounded-lg bg-green-700 px-6 py-3 text-white transition-opacity duration-300 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-green-500 ${
        isSigning ? 'cursor-not-allowed opacity-50' : 'opacity-100'
      }`}
      disabled={isSigning}
    >
      {isSigning ? 'Confirming...' : 'Confirm Wallet Ownership'}
    </button>
  );
}
